if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item'],
        };

        this.sliderInstance = false;
        this.thumbsInstance = false;
      }

      connectedCallback() {
        this.init();
      }

      init() {
        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.setSliderOptions();

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          this.initImageZoom();
        }
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);

        this.sliderOptions = {
          init: false,
          slidesPerView: '1',
          spaceBetween: mediaItemGap,
          loop: true,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'fraction',
          },
          threshold: 2,
        };

        this.thumbsOptions = {
          slidesPerView: 4,
          breakpoints: {
            461: {
              slidesPerView: 5,
            },
          },
          spaceBetween: mediaItemGap,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 2,
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions = Object.assign({}, this.thumbsOptions, {
              breakpoints: {
                768: {
                  direction: 'vertical',
                  slidesPerView: 'auto',
                },
              },
            });
            break;
          case 'slider-freemode':
            this.sliderOptions = Object.assign({}, this.sliderOptions, {
              slidesPerView: 'auto',
            });
            break;
        }
      }

      updateMediaLayout() {
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 2,
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs,
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();
        }
      }

      destroySlider() {
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      getVisibleMediaForZoom() {
        return [...this.querySelectorAll(
          '.product__media-item:not(.swiper-slide-duplicate)'
        )].filter(media => media.style.display !== 'none');
      }

      initImageZoom() {
        let dataSource = [];
        const allMedia = this.getVisibleMediaForZoom();
        if (allMedia) {
          allMedia.forEach((media) => {
            switch (media.dataset.mediaType) {
              case 'model':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('product-model').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'video':
              case 'external_video':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('video-element').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'image':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  src: media.dataset.src,
                  width: media.dataset.pswpWidth,
                  height: media.dataset.pswpHeight,
                  mediaType: media.dataset.mediaType,
                });
                break;
            }
          });
        }

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
        });

        this.lightbox.addFilter('thumbEl', (thumbEl, { id }, index) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el;
              }
            }
          }

          return thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data: { id } }) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el.src;
              }
            }
          }

          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { currIndex } = this.lightbox.pswp;
            if (this.sliderInstance.slider.realIndex !== currIndex) {
              this.sliderInstance.slider.slideToLoop(currIndex, 100, false);
            }
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox.pswp.currSlide.data.mediaType != 'image') {
            e.preventDefault();
          }
        });

        this.lightbox.on('uiRegister', () => {
          if (!this.onlyImage) {
            this.lightbox.pswp.ui.registerElement({
              name: 'next',
              ariaLabel: 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100" role="presentation"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.next();
              },
            });
            this.lightbox.pswp.ui.registerElement({
              name: 'prev',
              ariaLabel: 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100" role="presentation"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.prev();
              },
            });
          }
          this.lightbox.pswp.ui.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox.pswp.close();
            },
          });
        });

        this.lightbox.init();

        FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (e, media) => {
            if (media.dataset.mediaType === 'image') {
              const visibleMedia = this.getVisibleMediaForZoom();
              const index = visibleMedia.findIndex(
                m => m.dataset.mediaIndex === media.dataset.mediaIndex
              );

              if (index > -1) {
                this.lightbox.loadAndOpen(index);
              }
            }
          },
        });
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;

          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex, thumbs } = swiper;

          if (thumbs.swiper) {
            thumbs.swiper.slideTo(activeIndex);
          }

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);

            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      rebuildZoomGallery() {
        if (!this.enableImageZoom) return;

        // Close & destroy old instance
        if (this.lightbox) {
          try {
            this.lightbox.pswp?.close();
            this.lightbox.destroy();
          } catch (e) {}
          this.lightbox = null;
        }

        // Re-init zoom with ONLY visible media
        this.initImageZoom();
      }

      resetSwiperLoop() {
        const swiper = this.sliderInstance?.slider;
        if (!swiper) return;

        if (swiper.params.loop) {
          swiper.loopDestroy();
          swiper.params.loop = false;
        }

        swiper.updateSlides();
        swiper.update();
      }

      syncPaginationWithVisibleSlides() {
        const swiper = this.sliderInstance?.slider;
        if (!swiper) return;

        const visibleSlides = swiper.slides.filter(
          slide =>
            slide.style.display !== 'none' &&
            !slide.classList.contains('swiper-slide-duplicate')
        );

        const pagination = this.querySelector('.slider-controls .swiper-pagination');
        if (!pagination) return;

        const currentEl = pagination.querySelector('.swiper-pagination-current');
        const totalEl = pagination.querySelector('.swiper-pagination-total');

        if (currentEl) currentEl.textContent = 1;
        if (totalEl) totalEl.textContent = visibleSlides.length;
      }

      setActiveMedia(variant) {
        if (!variant) return;

        const variantId = String(variant.id);
        const wrapper = this.querySelector('.product__media-list');
        const allMedia = Array.from(this.querySelectorAll('.product__media-item'));

        /* = CASE 1: METAFIELD IMAGES = */
        const metafieldMedia = allMedia.filter(
          (media) =>
            media.hasAttribute('data-variant-metafield') &&
            media.dataset.variantId === variantId
        );

        if (metafieldMedia.length) {
          // Hide everything first
          allMedia.forEach((media) => (media.style.display = 'none'));

          // Show only selected variant metafield images
          metafieldMedia.forEach((media) => (media.style.display = ''));

          // Reorder: preserve metafield order on top
          const visibleSlides = [...metafieldMedia];
          const hiddenSlides = allMedia.filter((m) => m.style.display === 'none');

          wrapper.innerHTML = '';
          visibleSlides.forEach((media) => wrapper.appendChild(media));
          hiddenSlides.forEach((media) => wrapper.appendChild(media));

          // Slider reset: always start from first visible slide
          if (this.sliderInstance?.slider) {
            this.resetSwiperLoop?.();
            this.sliderInstance.slider.slideTo(0, 0); // first visible slide
            this.sliderInstance.slider.update();
          }

          this.rebuildZoomGallery();
          return; // STOP: metafield logic handled
        }

        /* ================= CASE 2: DEFAULT VARIANT IMAGES ================= */
        const featuredMediaId = variant.featured_media?.id;
        let featuredMediaEl = null;

        allMedia.forEach((media) => {
          const variantIds = media.getAttribute('data-variant-ids');
          const mediaId = media.dataset.mediaId;

          // Common images → always show
          if (!variantIds) {
            media.style.display = '';
            return;
          }

          const idsArray = variantIds.split(',');

          // Only selected variant images visible
          if (idsArray.includes(variantId)) {
            media.style.display = '';
            if (mediaId == featuredMediaId) featuredMediaEl = media;
          } else {
            media.style.display = 'none';
          }
        });

        /*REORDER: VISIBLE FIRST, HIDDEN LAST */
        const visibleSlides = [];
        const hiddenSlides = [];

        allMedia.forEach((media) => {
          if (media.style.display === 'none') hiddenSlides.push(media);
          else visibleSlides.push(media);
        });

        wrapper.innerHTML = '';

        // Featured first if visible
        if (featuredMediaEl && featuredMediaEl.style.display !== 'none') {
          wrapper.appendChild(featuredMediaEl);
        }

        // Other visible slides (excluding featured)
        visibleSlides.forEach((media) => {
          if (media !== featuredMediaEl) wrapper.appendChild(media);
        });

        // Hidden slides always last
        hiddenSlides.forEach((media) => wrapper.appendChild(media));

        /* ================= SWIPER RESET & SYNC ================= */
        if (this.sliderInstance?.slider) {
          this.resetSwiperLoop?.(); // required for loop mode

          // Always start from first visible slide
          const firstVisibleIndex = visibleSlides.findIndex(
            (m) => m.style.display !== 'none'
          );
          this.sliderInstance.slider.slideTo(firstVisibleIndex >= 0 ? firstVisibleIndex : 0, 0);
          this.sliderInstance.slider.update();
        }

        this.rebuildZoomGallery(); // update zoom gallery
      }

      sortMediaItems(variant) {
        let newMedias = this.elements.mediaItems;

        // Reset ordering.
        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        newMedias.some((media, index) => {
          if (media.dataset.mediaId == variant.featured_media.id) {
            const [element] = newMedias.splice(index, 1);
            newMedias.unshift(element);
            return true;
          }
        });

        this.elements.mediaList.innerHTML = '';
        newMedias.forEach((media) => {
          this.elements.mediaList.appendChild(media);
        });

        if (!FoxTheme.config.mqlMobile) {
          const selectedMedia = this.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
          if (selectedMedia) {
            window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
          }
        }
      }
    }
  );
}

class IngredientModalHandler {
  constructor(containerElement) {
    this.element = containerElement;
    this.lightbox = null;
    this.pswpItems = [];
    this.customCloseBtn = null;
  }

  initIngredientModals() {
    let dataSource = [];

    const openModalButtons = [...this.element.querySelectorAll('.data-open-pswp')];
    const allIngredientModalContents = [...this.element.querySelectorAll('.modal.information-popup')];
    if (!openModalButtons.length || !allIngredientModalContents.length) return;

    // Map modal content by their data-id for lookup
    const ingredientContentMap = new Map();
    allIngredientModalContents.forEach(modal => {
      ingredientContentMap.set(modal.dataset.id, modal);
    });

    // Populate the dataSource for PhotoSwipe
    openModalButtons.forEach((button) => {
      const modalId = button.dataset.btnId;
      const modalContent = ingredientContentMap.get(modalId);

      if (modalContent) {
        const clonedBody = modalContent.cloneNode(true);
        clonedBody.removeAttribute(`hidden`);
        clonedBody.setAttribute(`open`,``);
        clonedBody.setAttribute(`active`,``);
        dataSource.push({
          id: modalId,
          html: clonedBody.outerHTML,
          mediaType: 'html',
          width: 0,
          height: 0
        });
      }
    });

    this.pswpItems = dataSource;

    this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
      dataSource: this.pswpItems,
      pswpModule: window.FoxTheme.PhotoSwipe,
      initialZoomLevel: 'fill',
      bgOpacity: 0.5,
      closeOnOutsideClick: true,
      arrowPrev: false,
      arrowNext: false,
      zoom: false,
      close: false,
      counter: false,
      preloader: false
    });

    // Add filter to inject custom HTML content into the slide
    this.lightbox.addFilter('itemData', (itemData, index) => {
        // You might need to adjust itemData properties here if PhotoSwipe struggles with HTML.
        // For example, force type to 'html' if PhotoSwipe supports it explicitly.
        // itemData.type = 'html';
        return itemData;
    });

    // Register custom UI elements (Prev, Next, Counter)
    this.lightbox.on('uiRegister', () => {

      // Register custom Next button
      this.lightbox.pswp.ui.registerElement({
        name: 'next-ingredient',
        ariaLabel: 'Next ingredient',
        order: 3,
        isButton: true,
        html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100" role="presentation"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
        onClick: (event, el) => {
          this.lightbox.pswp.next();
        },
      });

      // Register custom prev button
      this.lightbox.pswp.ui.registerElement({
        name: 'prev-ingredient',
        ariaLabel: 'Previous ingredient',
        order: 2,
        isButton: true,
        html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100" role="presentation"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
        onClick: (event, el) => {
          this.lightbox.pswp.prev();
        },
      });

      // Register the counter element just before the prev/next buttons.
      this.lightbox.pswp.ui.registerElement({
        name: 'custom-ingredient-counter',
        order: 1,
        html: '',
        onInit: (el, pswp) => {
          // Update the counter text when the slide changes
          pswp.on('change', () => {
            const currentSlide = pswp.currSlide.data.id;
            const totalSlides = pswp.getNumItems();
            const currentIndex = this.pswpItems.findIndex(item => item.id === currentSlide);
            el.textContent = `${currentIndex + 1} of ${totalSlides} Ingredients`;
          });
        }
      });

      //add custom class to main pswp element. so that it doesn't conflict with other pswp
      if(this.lightbox?.pswp?.element){
        this.lightbox.pswp.element.classList.add(`pswp-ingredients-modal`);
      }

    });

    this.lightbox.on('afterInit', () => {
      const pswpContainer = this.lightbox?.pswp?.container;
      if(!pswpContainer) return;
      const parentElement = pswpContainer.parentElement;
      // Create the close button element
      this.customCloseBtn = document.createElement('button');
      this.customCloseBtn.classList.add('pswp__button', 'pswp-custom-close-btn');
      this.customCloseBtn.setAttribute('aria-label', 'Close ingredient modal');
      this.customCloseBtn.innerHTML = '<svg width="36" height="36" viewBox="0 0 46 36" fill="none" xmlns="http://www.w3.org/2000/svg" role="presentation"> <g filter="url(#7nb8qe8pia)"> <path d="m23.558 18.337 6.908-6.909a.833.833 0 0 0-1.175-1.175l-6.908 6.909-6.908-6.917a.837.837 0 1 0-1.184 1.183l6.917 6.909-6.917 6.908a.833.833 0 1 0 1.175 1.175l6.917-6.908 6.908 6.908a.833.833 0 0 0 1.175-1.175l-6.908-6.908z" fill="#2E2E2B"/> </g> <defs> <filter id="7nb8qe8pia" x="0" y="0" width="44.666" height="44.711" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"> <feFlood flood-opacity="0" result="BackgroundImageFix"/> <feColorMatrix in="SourceAlpha" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/> <feOffset dy="4"/> <feGaussianBlur stdDeviation="2"/> <feComposite in2="hardAlpha" operator="out"/> <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"/> <feBlend in2="BackgroundImageFix" result="effect1_dropShadow_4337_23106"/> <feBlend in="SourceGraphic" in2="effect1_dropShadow_4337_23106" result="shape"/></filter></defs></svg>';

      parentElement.appendChild(this.customCloseBtn);

      this.customCloseBtn.addEventListener('click', () => {
        this.lightbox.pswp.close();
      });

      //outside click close modal
      const pswpElement = this.lightbox?.pswp?.element;
      pswpElement.addEventListener('click', (event) => {
        if (event.target.classList.contains('pswp') || event.target.classList.contains('pswp__bg')) {
          this.lightbox.pswp.close();
        }
      });

      //custom mose wheel on pswp inner body modal element
      setTimeout(() => {
        const pswpItemsBody = pswpContainer.querySelectorAll('.drawer__body_ing');
        if(pswpItemsBody.length){
          pswpItemsBody.forEach(el => {
            // Prevent pswp from hijacking wheel events
            el.addEventListener('wheel', e => {
              e.stopPropagation();
            }, { passive: false });

            // Prevent pswp from hijacking touch events
            el.addEventListener('touchstart', e => {
              e.stopPropagation();
            }, { passive: false });

            el.addEventListener('touchmove', e => {
              e.stopPropagation();
            }, { passive: false });
          });
        }
      }, 950);

    });

    //CSS to hide overflow from body element
    this.lightbox.on('beforeOpen', () => {
      document.body.style.overflow = 'hidden';
    });

    //CSS to remove overflow from body element
    this.lightbox.on('close', () => {
      document.body.style.overflow = '';
    });

    // Initialize the lightbox.
    this.lightbox.init();

    // Attach click handlers to your buttons to open the PhotoSwipe gallery
    document.addEventListener('click', (e) => {
      const button = e.target.closest('.data-open-pswp');
      if (button) {
        e.preventDefault();
        const clickedModalId = button.dataset.btnId;
        // Find the correct item to open in PhotoSwipe
        const initialIndex = this.pswpItems.findIndex(item => item.id === clickedModalId);
        if (initialIndex !== -1) {
          this.lightbox.loadAndOpen(initialIndex); // Open PhotoSwipe at the correct slide
        }
      }
    });
  }
}

const ingPanels = document.querySelectorAll('.specific-ingredients-wrap');
if (ingPanels.length) {
  ingPanels.forEach(panel => {
    const handler = new IngredientModalHandler(panel);
    handler.initIngredientModals();
  });
}
